import { StyledList, StyledListHeader, StyledListItem } from '@/components/AssetsPage/PopoverList';
import { BodyText } from '@/components/BodyText';
import { HeaderText } from '@/components/HeaderText';
import { SHOW_HELP_PARAM, HELP_SECTION_PARAM } from '@/components/HelpContent/helpParameters';
import { HelpSection } from '@/components/HelpContent/HelpContent';
import { RoundedPopover } from '@/components/Popover/RoundedPopover';
import { usePopover } from '@/hooks/usePopover';
import { getSampleMetadataTemplateData } from '@/pages/MetadataBulkUploadPage/components/sampleTemplateData';
import { BulkUploadWizardPageObject } from '@/poms/bulkUploadWizardPageObjectModel';
import { createUseStyles } from '@/services/createUseStyles';
import { DownloadFormat, downloadSpreadsheet } from '@/utils/downloadUtils';
import { CloudUpload, InfoOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { Alert, Box, Button, Divider, IconButton, Link, ListItemIcon, Tooltip, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { BulkUploadData } from '../../types';
import { parseSpreadsheetFile } from '../../utils/fileParser';

const useStyles = createUseStyles({
  FileUploadStep_root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    gap: '24px',
  },
  FileUploadStep_header: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '16px',
  },
  FileUploadStep_headerActions: {
    display: 'flex',
    gap: '8px',
  },
  FileUploadStep_dropzone: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    border: '2px dashed #ccc',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    '&:hover': {
      borderColor: '#1976d2',
    },
    '&.active': {
      borderColor: '#1976d2',
      backgroundColor: '#e3f2fd',
    },
  },
  FileUploadStep_icon: {
    fontSize: '64px',
    color: '#999',
    marginBottom: '16px',
  },
  FileUploadStep_text: {
    textAlign: 'center',
    marginBottom: '16px',
  },
  FileUploadStep_button: {
    marginTop: '16px',
  },
  FileUploadStep_drawer: {
    '& .MuiDrawer-paper': {
      width: '400px',
      padding: '24px',
    },
  },
  FileUploadStep_drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
});

const downloadSampleTemplate = (format: DownloadFormat) => {
  const sampleData = getSampleMetadataTemplateData();
  const filename = `metadata-template.${format}`;

  downloadSpreadsheet(sampleData, { filename, format });
};

export const FileUploadStep = ({ onFileUploaded }: { onFileUploaded: (data: BulkUploadData) => void }) => {
  const c = useStyles();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [, setSearchParameters] = useSearchParams();

  const {
    open: openDownloadPopover,
    close: closeDownloadPopover,
    isOpen: isDownloadPopoverOpen,
    anchorElement: downloadAnchorElement,
  } = usePopover();

  const handleDownloadFormat = useCallback(
    (format: DownloadFormat) => {
      try {
        downloadSampleTemplate(format);
      } finally {
        closeDownloadPopover();
      }
    },
    [closeDownloadPopover]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      setIsProcessing(true);
      setError(undefined);

      parseSpreadsheetFile(file)
        .then(result => {
          onFileUploaded(result);
        })
        .catch(error => {
          setError(error instanceof Error ? error.message : 'Failed to parse file');
        })
        .finally(() => {
          setIsProcessing(false);
        });
    },
    [onFileUploaded]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    multiple: false,
    disabled: isProcessing,
  });

  return (
    <Box className={c.FileUploadStep_root}>
      <Box className={c.FileUploadStep_header} data-testid={BulkUploadWizardPageObject.FileUploadHeader}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BodyText
            data-testid={BulkUploadWizardPageObject.FileUploadTitle}
            sx={{
              color: 'text.primary',
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          >
            Bulk update asset metadata
          </BodyText>
          <Tooltip title='View detailed requirements'>
            <IconButton
              onClick={() => {
                setSearchParameters(
                  previous => {
                    const newParameters = new URLSearchParams(previous);
                    newParameters.set(SHOW_HELP_PARAM, '1');
                    newParameters.set(HELP_SECTION_PARAM, HelpSection.Metadata);
                    return newParameters;
                  },
                  { replace: true }
                );
              }}
              size='small'
              sx={{ p: 0.25 }}
              data-testid={BulkUploadWizardPageObject.RequirementsInfoButton}
            >
              <InfoOutlined sx={{ fontSize: '16px', color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ mb: 1 }}>
          <Link
            component='button'
            onClick={openDownloadPopover}
            sx={{
              color: 'primary.main',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 'normal',
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              textAlign: 'left',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            data-testid={BulkUploadWizardPageObject.DownloadTemplateButton}
          >
            Download a metadata template
          </Link>
          <RoundedPopover
            open={isDownloadPopoverOpen}
            anchorEl={downloadAnchorElement}
            onClose={closeDownloadPopover}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  marginTop: '4px',
                  borderRadius: '12px',
                },
              },
            }}
          >
            <StyledList>
              <StyledListHeader>
                <BodyText>Download Template</BodyText>
              </StyledListHeader>
              <Divider />
              <StyledListItem
                onClick={() => handleDownloadFormat('csv')}
                sx={{ cursor: 'pointer' }}
                data-testid={`${BulkUploadWizardPageObject.DownloadTemplateButton}-csv-option`}
              >
                <ListItemIcon>
                  <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
                </ListItemIcon>
                <BodyText>CSV</BodyText>
              </StyledListItem>
              <StyledListItem
                onClick={() => handleDownloadFormat('xlsx')}
                sx={{ cursor: 'pointer' }}
                data-testid={`${BulkUploadWizardPageObject.DownloadTemplateButton}-xlsx-option`}
              >
                <ListItemIcon>
                  <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
                </ListItemIcon>
                <BodyText>XLSX</BodyText>
              </StyledListItem>
            </StyledList>
          </RoundedPopover>
        </Box>
      </Box>
      {error && <Alert severity='error'>{error}</Alert>}
      <Box
        {...getRootProps()}
        className={`${c.FileUploadStep_dropzone} ${isDragActive ? 'active' : ''}`}
        data-testid={BulkUploadWizardPageObject.Dropzone}
      >
        <input {...getInputProps()} data-testid={BulkUploadWizardPageObject.DropzoneInput} />
        <CloudUpload className={c.FileUploadStep_icon} />
        <HeaderText className={c.FileUploadStep_text}>
          {isDragActive ? 'Drop the file here' : 'Drag & drop a file here, or click to select'}
        </HeaderText>
        <Typography
          variant='body2'
          color='textSecondary'
          className={c.FileUploadStep_text}
          data-testid={BulkUploadWizardPageObject.SupportedFormatsText}
        >
          Supported formats: CSV, XLSX, XLS • Maximum file size: 10MB
        </Typography>
        <Typography
          variant='body2'
          color='textSecondary'
          className={c.FileUploadStep_text}
          data-testid={BulkUploadWizardPageObject.TemplateHintText}
        >
          Need a template? Download a sample CSV above
        </Typography>
        <Button
          variant='contained'
          disabled={isProcessing}
          className={c.FileUploadStep_button}
          data-testid={BulkUploadWizardPageObject.SelectFileButton}
        >
          {isProcessing ? 'Processing...' : 'Select File'}
        </Button>
      </Box>
    </Box>
  );
};
